/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsNumbersPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Numbers management page - following Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Plus, Edit, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { ROUTES } from '../../../constants/routes';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { fetchNumbers, deleteNumber } from '../api';
import type { SmsNumber } from '../types';

interface NumberColumn {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: SmsNumber) => React.ReactNode;
}

const NUMBER_TYPE_LABELS: Record<string, string> = {
    long_code: 'Long Code',
    toll_free: 'Toll-Free',
    short_code: 'Short Code',
    alphanumeric: 'Alphanumeric',
};

export default function SmsNumbersPage() {
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const navigate = useNavigate();
    const [data, setData] = useState<SmsNumber[]>([]);
    const [loading, setLoading] = useState(true);
    const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; number: SmsNumber | null }>({
        isOpen: false,
        number: null,
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('phoneNumber');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Define columns
    const availableColumns = useMemo<NumberColumn[]>(() => [
        {
            key: 'phoneNumber',
            label: t('sms.numbers.columns.phoneNumber'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsNumber) => (
                <span className="font-mono font-medium text-primary">{item?.phoneNumber || '-'}</span>
            ),
        },
        {
            key: 'friendlyName',
            label: t('sms.numbers.columns.friendlyName'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsNumber) => item?.friendlyName || '-',
        },
        {
            key: 'numberType',
            label: t('sms.numbers.columns.type'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsNumber) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                    {NUMBER_TYPE_LABELS[item?.numberType] || item?.numberType || '-'}
                </span>
            ),
        },
        {
            key: 'provider',
            label: t('sms.numbers.columns.provider'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsNumber) => item?.provider?.name || '-',
        },
        {
            key: 'enabled',
            label: t('sms.numbers.columns.enabled'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsNumber) => (
                item?.enabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                )
            ),
        },
        {
            key: 'actions',
            label: t('common.actions'),
            sortable: false,
            visible: true,
        },
    ], [t, i18n.language]);

    const [columns, setColumns] = useState<NumberColumn[]>(availableColumns);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const numbers = await fetchNumbers();
            setData(numbers);
        } catch (error) {
            showError(error.message || t('sms.numbers.errorLoading'));
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [showError, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                item.phoneNumber.toLowerCase().includes(query) ||
                (item.friendlyName || '').toLowerCase().includes(query)
            );
        }

        result.sort((a, b) => {
            const aVal = (a as any)[sortBy] || '';
            const bVal = (b as any)[sortBy] || '';

            if (typeof aVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return result;
    }, [data, searchQuery, sortBy, sortOrder]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * limit;
        return filteredData.slice(start, start + limit);
    }, [filteredData, page, limit]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: filteredData.length,
        totalPages: Math.ceil(filteredData.length / limit),
    }), [page, limit, filteredData.length]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((key: string) => {
        if (sortBy === key) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev => prev.map(col =>
            col.key === key ? { ...col, visible: !col.visible } : col
        ));
    }, []);

    const handleCreate = useCallback(() => {
        navigate(ROUTES.SMS_NUMBERS_NEW);
    }, [navigate]);

    const handleEdit = useCallback((number: SmsNumber) => {
        const idToUse = number.uuid ?? number.id;
        navigate(ROUTES.SMS_NUMBER_DETAIL(idToUse));
    }, [navigate]);

    const handleDelete = useCallback((number: SmsNumber) => {
        setDeleteModal({ isOpen: true, number });
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!deleteModal.number) return;

        try {
            const idToUse = deleteModal.number.uuid ?? deleteModal.number.id;
            await deleteNumber(idToUse);
            showSuccess(t('sms.numbers.messages.deleteSuccess'));
            setDeleteModal({ isOpen: false, number: null });
            loadData();
        } catch (error) {
            showError(error.message || t('sms.numbers.messages.deleteError'));
        }
    }, [deleteModal.number, loadData, showError, showSuccess, t]);

    const renderActions = useCallback((number: SmsNumber) => (
        <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.edit')} position="top">
                <button
                    onClick={() => handleEdit(number)}
                    className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </button>
            </SimpleTooltip>

            <SimpleTooltip content={t('common.delete')} position="top">
                <button
                    onClick={() => handleDelete(number)}
                    className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                >
                    <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </button>
            </SimpleTooltip>
        </div>
    ), [t, handleEdit, handleDelete]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={paginatedData}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                renderActions={renderActions}
                searchPlaceholder={t('sms.numbers.searchPlaceholder')}
                title={t('sms.numbers.title')}
                titleIcon={<Phone className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('sms.numbers.actions.add')} position="top">
                            <button
                                onClick={handleCreate}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('sms.numbers.actions.add')}
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, number: null })}
                onConfirm={confirmDelete}
                title={t('sms.numbers.deleteTitle')}
                message={`${t('sms.numbers.messages.confirmDelete')} (${deleteModal.number?.phoneNumber})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
            />
        </div>
    );
}
